<template>
  <div>
    <dx-grid
      ref="dxGrid"
      url="alumini"
      :columns="columns"
      :title="title"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      :actions="actions"
    ></dx-grid>

    <!-- Initial Setup Dialog -->
    <v-dialog v-model="setupDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="success white--text">
          <v-icon color="white" class="mr-2">mdi-cog</v-icon>
          <span class="headline">Setup Batch Entry</span>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-container class="pa-0">
            <v-layout column>
              <v-flex xs12 class="mb-2">
                <v-autocomplete
                  v-model="batchSettings.year"
                  :items="yearsList"
                  label="Year (RegID) *"
                  outlined
                  prepend-icon="mdi-calendar"
                  :rules="[rules.required]"
                ></v-autocomplete>
              </v-flex>

              <v-flex xs12 class="mb-2">
                <v-autocomplete
                  v-model="batchSettings.class"
                  :items="classList"
                  label="Class *"
                  outlined
                  prepend-icon="mdi-school"
                  :rules="[rules.required]"
                ></v-autocomplete>
              </v-flex>

              <v-flex xs12 class="mb-2">
                <v-text-field
                  v-model="batchSettings.section"
                  label="Section *"
                  outlined
                  prepend-icon="mdi-format-list-numbered"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 class="mb-2">
                <v-autocomplete
                  v-model="batchSettings.branch"
                  :items="branchList"
                  label="Branch"
                  outlined
                  prepend-icon="mdi-office-building"
                ></v-autocomplete>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn dark color="default" @click="closeSetupDialog">Cancel</v-btn>
          <v-btn
            color="success"
            :disabled="!isSetupValid()"
            @click="startBatchEntry"
          >
            <v-icon left>mdi-play</v-icon>
            Start Entry
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Entry Dialog with Sidebar -->
    <v-dialog v-model="addDialog" max-width="1100px" persistent scrollable>
      <v-card>
        <v-card-title class="success white--text py-0">
          <span class="headline">
            <v-icon color="white" class="mr-2">{{
              isEditMode ? "mdi-pencil" : "mdi-account-plus"
            }}</v-icon>
            {{ isEditMode ? "Edit Alumini Record" : "Add New Alumini" }}
          </span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-layout row wrap>
          <!-- Main Form Section -->
          <v-flex xs12 md9>
            <v-card-text class="px-4 pb-0">
              <v-form ref="aluminiForm">
                <v-container class="pa-0">
                  <v-layout row wrap>
                    <!-- Batch Settings Display (Top of form) -->
                    <v-flex xs12 v-if="!isEditMode && batchSettings.year">
                      <v-alert
                        color="success"
                        value="true"
                        dark
                        dense
                        outline
                        class="mb-2"
                      >
                        <v-layout align-center>
                          <v-icon small class="mr-2"
                            >mdi-information-outline</v-icon
                          >
                          <strong class="mr-2">Batch Entry:</strong>
                          {{ batchSettings.year }} | {{ batchSettings.class }} |
                          Section {{ batchSettings.section
                          }}<span v-if="batchSettings.branch">
                            | {{ batchSettings.branch }}</span
                          >
                        </v-layout>
                      </v-alert>
                    </v-flex>

                    <!-- SECTION 1: Primary Information (Prominent) -->
                    <v-flex xs12>
                      <div class="subheading font-weight-medium mb-0">
                        Primary Information
                      </div>
                    </v-flex>

                    <!-- Year/Class/Section - Only in edit mode -->
                    <v-flex xs12 sm4 v-if="isEditMode" class="py-0">
                      <v-autocomplete
                        v-model="newAlumini.year"
                        :items="yearsList"
                        label="Year (RegID) *"
                        :rules="[rules.required]"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-calendar"
                        @change="onYearChanged"
                        class="compact-field"
                      ></v-autocomplete>
                    </v-flex>

                    <v-flex xs12 sm4 v-if="isEditMode" class="py-0">
                      <v-autocomplete
                        v-model="newAlumini.class"
                        :items="classList"
                        label="Class *"
                        :rules="[rules.required]"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-school"
                        class="compact-field"
                      ></v-autocomplete>
                    </v-flex>

                    <v-flex xs12 sm4 v-if="isEditMode" class="py-0">
                      <v-text-field
                        v-model="newAlumini.section"
                        label="Section *"
                        :rules="[rules.required]"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-text-box"
                        class="compact-field"
                      ></v-text-field>
                    </v-flex>

                    <!-- Branch - Only in edit mode -->
                    <v-flex xs12 sm6 v-if="isEditMode" class="py-0">
                      <v-autocomplete
                        v-model="newAlumini.branch"
                        :items="branchList"
                        label="Branch"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-office-building"
                        class="compact-field"
                      ></v-autocomplete>
                    </v-flex>

                    <!-- Spacer for alignment (always show to keep Last Name and First Name together) -->
                    <v-flex xs12 sm6 v-if="isEditMode"></v-flex>

                    <!-- English Names - Required (Last Name First) -->
                    <v-flex xs12 sm6 class="py-0">
                      <v-text-field
                        v-model="newAlumini.lastEnglish"
                        label="Last Name (English) *"
                        :rules="[rules.required]"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-account-circle"
                        class="compact-field"
                      ></v-text-field>
                    </v-flex>

                    <v-flex xs12 sm6 class="py-0">
                      <v-text-field
                        v-model="newAlumini.engName"
                        label="First Name (English) *"
                        :rules="[rules.required]"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-account-circle"
                        class="compact-field"
                      ></v-text-field>
                    </v-flex>

                    <v-flex xs12 class="py-2">
                      <v-divider></v-divider>
                    </v-flex>

                    <!-- SECTION 2: Person Lookups with Mini-Cards -->
                    <v-flex xs12>
                      <div class="subheading font-weight-medium mb-0">
                        Person Information
                      </div>
                    </v-flex>

                    <!-- Father Person ID with Mini-Card -->
                    <v-flex xs12 sm6 class="py-0">
                      <v-text-field
                        v-model="newAlumini.fatherPersonID"
                        label="Father Person ID"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-account-box"
                        readonly
                        @click="openPersonSearch"
                        clearable
                        @click:clear="clearFatherPerson"
                        placeholder="Click to search"
                        class="compact-field"
                      ></v-text-field>

                      <!-- Mini-Card for Selected Father (directly below Father Person ID) -->
                      <v-card
                        v-if="selectedPerson"
                        class="mini-person-card mt-2"
                        outlined
                        flat
                      >
                        <v-card-text class="pa-2">
                          <v-layout align-center>
                            <div class="person-label mr-2">
                              <strong class="caption">PERSON:</strong>
                            </div>
                            <v-icon x-small color="success" class="mr-1"
                              >mdi-account-box</v-icon
                            >
                            <div class="flex-grow-1 caption">
                              <strong
                                >{{ selectedPerson.lastEnglish }},
                                {{ selectedPerson.firstEnglish }}</strong
                              >
                              <span
                                v-if="selectedPerson.firstHebrew"
                                class="grey--text ml-1"
                              >
                                / {{ selectedPerson.lastHebrew }},
                                {{ selectedPerson.firstHebrew }}
                              </span>
                              <div class="grey--text">
                                {{ selectedPerson.address1 }}
                              </div>
                            </div>
                            <v-btn icon x-small @click="reopenPersonSearch">
                              <v-icon x-small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon x-small @click="clearFatherPerson">
                              <v-icon x-small>mdi-close</v-icon>
                            </v-btn>
                          </v-layout>
                        </v-card-text>
                      </v-card>
                    </v-flex>

                    <!-- UTA Family ID -->
                    <v-flex xs12 sm6 class="py-0">
                      <v-text-field
                        v-model="newAlumini.familyID"
                        label="UTA Family ID"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-account-group"
                        readonly
                        @click="openFamilySearch"
                        clearable
                        @click:clear="clearFamilyID"
                        placeholder="Click to search"
                        class="compact-field"
                      ></v-text-field>

                      <!-- Mini-Card for Selected Family (directly below UTA Family ID) -->
                      <v-card
                        v-if="selectedFamily"
                        class="mini-person-card mt-2"
                        outlined
                        flat
                      >
                        <v-card-text class="pa-2">
                          <v-layout align-center>
                            <div class="person-label mr-2">
                              <strong class="caption">UTA:</strong>
                            </div>
                            <v-icon x-small color="success" class="mr-1"
                              >mdi-account</v-icon
                            >
                            <div class="flex-grow-1 caption">
                              <strong
                                >{{ selectedFamily.lastName }},
                                {{ selectedFamily.firstName }}</strong
                              >
                              <span
                                v-if="selectedFamily.firstNameH"
                                class="grey--text ml-1"
                              >
                                / {{ selectedFamily.lastNameH }},
                                {{ selectedFamily.firstNameH }}
                              </span>
                              <div class="grey--text">
                                {{ selectedFamily.address1 }}
                              </div>
                            </div>
                            <v-btn icon x-small @click="reopenFamilySearch">
                              <v-icon x-small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon x-small @click="clearFamilyID">
                              <v-icon x-small>mdi-close</v-icon>
                            </v-btn>
                          </v-layout>
                        </v-card-text>
                      </v-card>
                    </v-flex>

                    <v-flex xs12 class="py-2">
                      <v-divider></v-divider>
                    </v-flex>

                    <!-- SECTION 3: Additional Information (Bottom) -->
                    <v-flex xs12>
                      <div class="subheading font-weight-medium mb-0">
                        Additional Information
                      </div>
                    </v-flex>

                    <!-- Student ID -->
                    <v-flex xs12 sm6 class="py-0">
                      <v-text-field
                        v-model="newAlumini.studentID"
                        label="Student ID"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-account"
                        class="compact-field pt-0"
                      ></v-text-field>
                    </v-flex>

                    <!-- DOB -->
                    <v-flex xs12 sm6 class="py-0">
                      <v-menu
                        v-model="dobMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            v-model="newAlumini.dob"
                            label="Date of Birth"
                            prepend-icon="mdi-calendar"
                            readonly
                            outlined
                            dense
                            hide-details
                            clearable
                            v-on="on"
                            class="compact-field pt-0"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="newAlumini.dob"
                          @input="dobMenu = false"
                          :picker-date.sync="pickerDate"
                          no-title
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>

                    <!-- Hebrew Names (Last Name First) -->
                    <v-flex xs12 sm6 class="py-0 mb-2">
                      <v-text-field
                        v-model="newAlumini.lastHebrew"
                        label="Last Name (Hebrew)"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-account-circle"
                        class="compact-field"
                      ></v-text-field>
                    </v-flex>

                    <v-flex xs12 sm6 class="py-0">
                      <v-text-field
                        v-model="newAlumini.firstHebrew"
                        label="First Name (Hebrew)"
                        outlined
                        dense
                        hide-details
                        prepend-icon="mdi-account-circle"
                        class="compact-field"
                      ></v-text-field>
                    </v-flex>
                  </v-layout>

                  <v-alert
                    :value="errorMessage"
                    type="error"
                    dense
                    class="mt-2 mb-0"
                    dismissible
                    @input="errorMessage = ''"
                  >
                    {{ errorMessage }}
                  </v-alert>

                  <v-alert
                    :value="successMessage"
                    type="success"
                    dense
                    class="mt-2 mb-0"
                    dismissible
                    @input="successMessage = ''"
                  >
                    {{ successMessage }}
                  </v-alert>
                </v-container>
              </v-form>
            </v-card-text>
          </v-flex>

          <!-- Sidebar: Recently Added Records -->
          <v-flex xs12 md3 v-if="!isEditMode" class="sidebar-section">
            <v-card flat class="pa-3" color="grey lighten-5">
              <div class="subheading font-weight-medium mb-3">
                <v-icon small class="mr-2">mdi-history</v-icon>
                Recently Added ({{ recentlyAdded.length }})
              </div>

              <div
                v-if="recentlyAdded.length === 0"
                class="text-center grey--text caption py-4"
              >
                No records added yet
              </div>

              <div v-else class="recent-list pa-0">
                <div
                  v-for="(record, index) in recentlyAdded"
                  :key="index"
                  class="recent-item mb-2 px-3 py-2"
                  style="display: flex; align-items: flex-start;"
                >
                  <div style="flex: 1;">
                    <div
                      class="font-weight-bold mb-2"
                      style="font-size: 13px; color: #000;"
                    >
                      {{ record.lastEnglish }}, {{ record.engName }}
                    </div>
                    <div style="font-size: 12px; color: #666;">
                      <div v-if="record.studentID" class="mb-1">
                        <v-icon x-small class="mr-1">mdi-account</v-icon>
                        ID: {{ record.studentID }}
                      </div>
                      <div v-if="record.branch" class="mb-1">
                        <v-icon x-small class="mr-1"
                          >mdi-office-building</v-icon
                        >
                        {{ record.branch }}
                      </div>
                      <div v-if="record.fatherPersonID" class="mb-1">
                        <v-icon x-small class="mr-1">mdi-account-box</v-icon>
                        Person: {{ record.fatherPersonID }}
                      </div>
                      <div class="mt-1">
                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                        {{ formatTime(record.addedAt) }}
                      </div>
                    </div>
                  </div>
                  <v-btn
                    icon
                    small
                    color="success"
                    @click="onRecentRecordClicked(record)"
                    class="ml-2"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-flex>
        </v-layout>

        <!-- Fixed Bottom Actions Bar -->
        <v-card-actions class="pa-3 grey lighten-4 mx-0 mt-2">
          <v-btn
            v-if="isEditMode"
            color="error"
            text
            small
            :loading="deleting"
            @click="confirmDelete"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete
          </v-btn>
          <small class="grey--text caption ml-2">* Required fields</small>
          <v-spacer></v-spacer>
          <v-btn dark color="default" text @click="closeDialog">
            {{ isEditMode ? "Cancel" : "Close Session" }}
          </v-btn>
          <v-btn
            v-if="!isEditMode"
            color="success"
            outline
            text
            :loading="saving"
            :disabled="!isFormValid()"
            @click="saveAluminiAndClose()"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save & Close
          </v-btn>
          <v-btn
            v-if="!isEditMode"
            color="success"
            :loading="saving"
            :disabled="!isFormValid()"
            @click="saveAlumini(true)"
          >
            <v-icon left>mdi-content-save-plus</v-icon>
            Save & Next
          </v-btn>
          <v-btn
            v-if="isEditMode"
            color="success"
            :loading="saving"
            :disabled="!isFormValid()"
            @click="saveAlumini(false)"
          >
            <v-icon left>mdi-content-save</v-icon>
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Person Search Dialog -->
    <v-dialog v-model="personSearchDialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="success white--text py-2">
          <span class="headline">Search Person</span>
          <v-spacer></v-spacer>
          <v-btn class="my-0 white--text" icon @click="closePersonSearch">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Search Type Toggle -->
          <v-btn-toggle
            v-model="personSearchType"
            mandatory
            class="mb-4"
            color="success"
          >
            <v-btn value="phone" small>
              <v-icon left small>mdi-phone</v-icon>
              Phone
            </v-btn>
            <v-btn value="name" small>
              <v-icon left small>mdi-account</v-icon>
              Name
            </v-btn>
            <v-btn value="address" small>
              <v-icon left small>mdi-home</v-icon>
              Address
            </v-btn>
          </v-btn-toggle>

          <!-- Phone Search -->
          <v-layout row wrap v-if="personSearchType === 'phone'">
            <v-flex xs12 sm6>
              <v-text-field
                v-model="personPhoneSearch"
                label="Phone Number"
                outlined
                dense
                prepend-icon="mdi-phone"
                @input="searchPersonByPhone"
                clearable
                placeholder="Enter phone number"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Name Search -->
          <v-layout row wrap v-if="personSearchType === 'name'">
            <v-flex xs12 class="mb-3">
              <v-btn-toggle
                v-model="personSearchNameMode"
                mandatory
                class="search-toggle"
                color="success"
              >
                <v-btn value="yiddish" small>Yiddish</v-btn>
                <v-btn value="english" small>English</v-btn>
              </v-btn-toggle>
            </v-flex>
            <v-flex xs12 sm6 class="pr-sm-2">
              <v-text-field
                v-model="personLastNameSearch"
                :label="
                  personSearchNameMode === 'yiddish'
                    ? 'Last Name (Hebrew)'
                    : 'Last Name (English)'
                "
                outlined
                dense
                prepend-icon="mdi-account-outline"
                @input="searchPersonByName"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-sm-2">
              <v-text-field
                v-model="personFirstNameSearch"
                :label="
                  personSearchNameMode === 'yiddish'
                    ? 'First Name (Hebrew)'
                    : 'First Name (English)'
                "
                outlined
                dense
                prepend-icon="mdi-account"
                @input="searchPersonByName"
                clearable
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap v-if="personSearchType === 'address'">
            <v-flex xs12 sm6 class="pr-sm-2">
              <v-text-field
                v-model="personAddressSearch"
                label="Street Address"
                outlined
                dense
                prepend-icon="mdi-home"
                @input="searchPersonByAddress"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-sm-2">
              <v-text-field
                v-model="personCitySearch"
                label="City"
                outlined
                dense
                prepend-icon="mdi-city"
                @input="searchPersonByAddress"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pr-sm-2">
              <v-text-field
                v-model="personZipSearch"
                label="ZIP Code"
                outlined
                dense
                prepend-icon="mdi-mailbox"
                @input="searchPersonByAddress"
                clearable
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- No Results Message -->
          <v-alert
            v-if="showNoPersonsMessage"
            type="info"
            class="mt-3"
            outlined
          >
            No persons found matching your search criteria
          </v-alert>

          <!-- Results List -->
          <div
            v-if="searchedPersons.length > 0"
            class="person-list-container mt-3"
          >
            <v-list class="person-list" two-line>
              <v-list-tile
                v-for="person in searchedPersons"
                :key="person.personID"
                @click="onPersonSelected(person)"
                class="person-item"
              >
                <v-list-tile-content>
                  <v-list-tile-title class="person-name mb-1 pb-2">
                    <span class="font-weight-medium">
                      {{ person.lastEnglish }}, {{ person.firstEnglish }}
                    </span>
                    <span
                      v-if="person.firstHebrew || person.lastHebrew"
                      class="grey--text ml-2"
                    >
                      / {{ person.lastHebrew }}, {{ person.firstHebrew }}
                    </span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title class="person-details mt-1">
                    <div class="detail-line">
                      <v-icon small class="detail-icon">mdi-account</v-icon>
                      <span>{{ person.personID }}</span>
                    </div>
                    <div v-if="person.address1" class="detail-line">
                      <v-icon small class="detail-icon">mdi-home</v-icon>
                      <span>
                        {{ person.address1
                        }}<span v-if="person.city">, {{ person.city }}</span
                        ><span v-if="person.zip"> {{ person.zip }}</span>
                      </span>
                    </div>
                    <div
                      v-if="person.father || person.fatherInLaw"
                      class="detail-line"
                    >
                      <span v-if="person.father" class="mr-3">
                        <v-icon small class="detail-icon"
                          >mdi-account-tie</v-icon
                        >
                        Father: {{ person.father }}
                      </span>
                      <span v-if="person.fatherInLaw">
                        <v-icon small class="detail-icon"
                          >mdi-account-tie-outline</v-icon
                        >
                        Shver: {{ person.fatherInLaw }}
                      </span>
                    </div>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="success">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Family Search Dialog -->
    <v-dialog v-model="familySearchDialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="success white--text py-2">
          <span class="headline">Search Account</span>
          <v-spacer></v-spacer>
          <v-btn class="my-0 white--text" icon @click="closeFamilySearch">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Search Type Toggle -->
          <v-btn-toggle
            v-model="familySearchType"
            mandatory
            class="mb-4"
            color="success"
          >
            <v-btn value="phone" small>
              <v-icon left small>mdi-phone</v-icon>
              Phone
            </v-btn>
            <v-btn value="name" small>
              <v-icon left small>mdi-account</v-icon>
              Name
            </v-btn>
            <v-btn value="address" small>
              <v-icon left small>mdi-home</v-icon>
              Address
            </v-btn>
          </v-btn-toggle>

          <!-- Phone Search -->
          <v-layout row wrap v-if="familySearchType === 'phone'">
            <v-flex xs12 sm6>
              <v-text-field
                v-model="familyPhoneSearch"
                label="Phone Number"
                outlined
                dense
                prepend-icon="mdi-phone"
                @input="searchFamilyByPhone"
                clearable
                placeholder="Enter phone number"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Name Search -->
          <v-layout row wrap v-if="familySearchType === 'name'">
            <v-flex xs12 class="mb-3">
              <v-btn-toggle
                v-model="familySearchNameMode"
                mandatory
                class="search-toggle"
                color="success"
              >
                <v-btn value="yiddish" small>Yiddish</v-btn>
                <v-btn value="english" small>English</v-btn>
              </v-btn-toggle>
            </v-flex>
            <v-flex xs12 sm6 class="pr-sm-2">
              <v-text-field
                v-model="familyLastNameSearch"
                :label="
                  familySearchNameMode === 'yiddish'
                    ? 'Last Name (Hebrew)'
                    : 'Last Name (English)'
                "
                outlined
                dense
                prepend-icon="mdi-account-outline"
                @input="searchFamilyByName"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-sm-2">
              <v-text-field
                v-model="familyFirstNameSearch"
                :label="
                  familySearchNameMode === 'yiddish'
                    ? 'First Name (Hebrew)'
                    : 'First Name (English)'
                "
                outlined
                dense
                prepend-icon="mdi-account"
                @input="searchFamilyByName"
                clearable
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap v-if="familySearchType === 'address'">
            <v-flex xs12 sm6 class="pr-sm-2">
              <v-text-field
                v-model="familyAddressSearch"
                label="Street Address"
                outlined
                dense
                prepend-icon="mdi-home"
                @input="searchFamilyByAddress"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-sm-2">
              <v-text-field
                v-model="familyCitySearch"
                label="City"
                outlined
                dense
                prepend-icon="mdi-city"
                @input="searchFamilyByAddress"
                clearable
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pr-sm-2">
              <v-text-field
                v-model="familyZipSearch"
                label="ZIP Code"
                outlined
                dense
                prepend-icon="mdi-mailbox"
                @input="searchFamilyByAddress"
                clearable
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- No Results Message -->
          <v-alert v-if="showNoFamilyMessage" type="info" class="mt-3" outlined>
            No accounts found matching your search criteria
          </v-alert>

          <!-- Results List -->
          <div
            v-if="searchedFamilies.length > 0"
            class="person-list-container mt-3"
          >
            <v-list class="person-list" two-line>
              <v-list-tile
                v-for="family in searchedFamilies"
                :key="family.accountID"
                @click="onFamilySelected(family)"
                class="person-item"
              >
                <v-list-tile-content>
                  <v-list-tile-title class="person-name mb-1 pb-2">
                    <span class="font-weight-medium">
                      {{ family.lastName }}, {{ family.firstName }}
                    </span>
                    <span
                      v-if="family.firstNameH || family.lastNameH"
                      class="grey--text ml-2"
                    >
                      / {{ family.lastNameH }}, {{ family.firstNameH }}
                    </span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title class="person-details mt-1">
                    <div class="detail-line">
                      <v-icon small class="detail-icon">mdi-account</v-icon>
                      <span>{{ family.accountID }}</span>
                    </div>
                    <div v-if="family.address1" class="detail-line">
                      <v-icon small class="detail-icon">mdi-home</v-icon>
                      <span>
                        {{ family.address1
                        }}<span v-if="family.cityStateZip"
                          >, {{ family.cityStateZip }}</span
                        >
                      </span>
                    </div>
                    <div
                      v-if="family.father || family.shver"
                      class="detail-line"
                    >
                      <span v-if="family.father" class="mr-3">
                        <v-icon small class="detail-icon"
                          >mdi-account-tie</v-icon
                        >
                        Father: {{ family.father }}
                      </span>
                      <span v-if="family.shver">
                        <v-icon small class="detail-icon"
                          >mdi-account-tie-outline</v-icon
                        >
                        Shver: {{ family.shver }}
                      </span>
                    </div>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="success">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Options Dialog -->
    <v-dialog v-model="editOptionsDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="success white--text">
          <span class="headline">
            <v-icon color="white" class="mr-2">mdi-cog</v-icon>
            Alumini Options
          </span>
        </v-card-title>

        <v-card-text class="pa-4">
          <div class="title mb-3">What would you like to do?</div>

          <v-layout column>
            <v-btn
              large
              color="success"
              class="mb-3"
              @click="goToDinnerView"
              :disabled="!currentRow || !currentRow.fatherPersonID"
            >
              <v-icon left>mdi-account-arrow-right</v-icon>
              Go to Account View
            </v-btn>

            <v-btn large outline color="success" @click="openEditDialog">
              <v-icon left>mdi-pencil</v-icon>
              Update Record
            </v-btn>
          </v-layout>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeEditOptionsDialog">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteConfirmDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="error white--text">
          <v-icon color="white" class="mr-2">mdi-alert</v-icon>
          Confirm Delete
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="title mb-2">
            Are you sure you want to delete this record?
          </div>
          <div class="body-1">This action cannot be undone.</div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn dark color="default" @click="deleteConfirmDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteAlumini">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
//import { formatCurrency } from "../utils/helpers.js";
export default {
  data: () => ({
    title: "Alumini",
    setupDialog: false,
    addDialog: false,
    editOptionsDialog: false,
    deleteConfirmDialog: false,
    dobMenu: false,
    saving: false,
    deleting: false,
    errorMessage: "",
    successMessage: "",
    pickerDate: null,
    isEditMode: false,
    currentRow: null,
    batchSettings: {
      year: null,
      class: "H-11",
      section: "",
      branch: null,
    },
    recentlyAdded: [],
    newAlumini: {
      id: null,
      year: null,
      familyID: "",
      studentID: "",
      fatherPersonID: "",
      class: "H-11",
      section: "",
      branch: null,
      firstHebrew: "",
      lastHebrew: "",
      engName: "",
      dob: null,
      lastEnglish: "",
    },
    yearsList: [],
    branchList: [],
    classList: ["H-11"],
    personSearchDialog: false,
    personSearchType: "phone",
    personSearchNameMode: "english",
    personPhoneSearch: "",
    personFirstNameSearch: "",
    personLastNameSearch: "",
    personAddressSearch: "",
    personCitySearch: "",
    personZipSearch: "",
    searchedPersons: [],
    showNoPersonsMessage: false,
    personSearchDebounceTimer: null,
    selectedPerson: null,
    familySearchDialog: false,
    familySearchType: "phone",
    familySearchNameMode: "english",
    familyPhoneSearch: "",
    familyFirstNameSearch: "",
    familyLastNameSearch: "",
    familyAddressSearch: "",
    familyCitySearch: "",
    familyZipSearch: "",
    searchedFamilies: [],
    showNoFamilyMessage: false,
    familySearchDebounceTimer: null,
    selectedFamily: null,
    rules: {
      required: (value) => !!value || "Required field",
    },
    actions: [],
    columns: [
      {
        field: "isMappedIcon",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        headerFilter: {
          allowSearch: false,
          groupInterval: undefined,
          height: undefined,
          searchMode: "contains",
          width: undefined,
        },
        icon: (value, row) => {
          return value ? "mdi-check-circle" : "mdi-alert";
        },
        iconColor: (value, row) => {
          return value ? "success" : "#f35123";
        },
      },
      {
        field: "firstHebrew",
        label: "First Hebrew",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "lastHebrew",
        label: "Last Hebrew",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "engName",
        label: "First English",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "lastEnglish",
        label: "Last English",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "regID",
        label: "Last Term",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "isMapped",
        label: "Is Mapped",
        format: "",
        type: "boolean",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "class",
        label: "Class",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "section",
        label: "Section",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "branch",
        label: "Branch",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "familyID",
        label: "UTA Family ID",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "studentID",
        label: "Student ID",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "fatherPersonID",
        label: "Father Person ID",
        link: "DinnerAccountView",
        format: "",
        template: "link-cell-template",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "weddingYear",
        label: "Wedding Year",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "DOB",
        field: "dob",
        type: "date",
        format: "MM/dd/yyyy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "fatherInLaw",
        label: "Mapped To Father In Law",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "father",
        label: "Mapped To Father",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Mapped To Person ID",
        field: "mappedToPersonID",
        link: "DinnerAccountView",
        format: "",
        template: "link-cell-template",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Mapped To Name",
        field: "mappedToName",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Mapped To Hebrew Name",
        field: "mappedToNameH",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "type",
        label: "Mapped To Type",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "address1",
        label: "Mapped To Address",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Mapped To Home Phone",
        field: "mappedToHomePhone",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Created On",
        field: "createdOn",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Created By",
        field: "createdBy",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  watch: {
    personSearchType() {
      // Clear search fields and results when switching search modes
      this.personPhoneSearch = "";
      this.personFirstNameSearch = "";
      this.personLastNameSearch = "";
      this.personAddressSearch = "";
      this.personCitySearch = "";
      this.personZipSearch = "";
      this.searchedPersons = [];
      this.showNoPersonsMessage = false;
      if (this.personSearchDebounceTimer) {
        clearTimeout(this.personSearchDebounceTimer);
      }
    },
    personSearchNameMode() {
      // Re-search when switching between Yiddish and English
      if (this.personFirstNameSearch || this.personLastNameSearch) {
        this.searchPersonByName();
      }
    },
    familySearchType() {
      // Clear search fields and results when switching search modes
      this.familyPhoneSearch = "";
      this.familyFirstNameSearch = "";
      this.familyLastNameSearch = "";
      this.familyAddressSearch = "";
      this.familyCitySearch = "";
      this.familyZipSearch = "";
      this.searchedFamilies = [];
      this.showNoFamilyMessage = false;
      if (this.familySearchDebounceTimer) {
        clearTimeout(this.familySearchDebounceTimer);
      }
    },
    familySearchNameMode() {
      // Re-search when switching between Yiddish and English
      if (this.familyFirstNameSearch || this.familyLastNameSearch) {
        this.searchFamilyByName();
      }
    },
  },
  created() {
    this.generateYearsList();
    this.loadBranchList();

    this.actions.push({
      text: "Add New Alumini",
      action: this.openAddDialog,
    });
  },
  methods: {
    onRowClicked(row) {
      // If createdOn has a value, show edit options dialog
      if (row.createdOn) {
        this.currentRow = row;
        this.editOptionsDialog = true;
      } else {
        // Otherwise, go to dinner account view as before
        this.$router.push({
          name: "DinnerAccountView",
          params: {
            id: row.fatherPersonID,
          },
          query: {
            aluminiStudent: row.studentID || row.id,
            limitView: true,
          },
        });
      }
    },
    onRecentRecordClicked(record) {
      // Same behavior as clicking a row in the grid
      this.currentRow = record;
      this.editOptionsDialog = true;
    },
    openAddDialog() {
      this.isEditMode = false;
      this.setupDialog = true;

      // Reset validation
      this.$nextTick(() => {
        if (this.$refs.aluminiForm) {
          this.$refs.aluminiForm.resetValidation();
        }
      });
    },
    closeSetupDialog() {
      this.setupDialog = false;
      this.batchSettings = {
        year: null,
        class: "H-11",
        section: "",
        branch: null,
      };
    },
    isSetupValid() {
      return (
        this.batchSettings.year &&
        this.batchSettings.class &&
        this.batchSettings.section
      );
    },
    startBatchEntry() {
      if (!this.isSetupValid()) {
        return;
      }

      // Lock settings and open main dialog
      this.setupDialog = false;
      this.recentlyAdded = [];
      this.resetForm();

      // Pre-fill form with batch settings
      this.newAlumini.year = this.batchSettings.year;
      this.newAlumini.class = this.batchSettings.class;
      this.newAlumini.section = this.batchSettings.section;
      this.newAlumini.branch = this.batchSettings.branch;

      // Update picker date based on year
      this.onYearChanged(this.batchSettings.year);

      this.addDialog = true;

      // Reset validation on next tick after dialog opens
      this.$nextTick(() => {
        if (this.$refs.aluminiForm) {
          this.$refs.aluminiForm.resetValidation();
        }
      });
    },
    async openEditDialog() {
      this.isEditMode = true;
      this.editOptionsDialog = false;

      // Clear selected person/family initially
      this.selectedPerson = null;
      this.selectedFamily = null;

      // Format DOB to YYYY-MM-DD format if it exists
      let formattedDob = null;
      if (this.currentRow.dob) {
        try {
          const date = new Date(this.currentRow.dob);
          if (!isNaN(date.getTime())) {
            // Format as YYYY-MM-DD
            formattedDob = date.toISOString().split("T")[0];
          }
        } catch (e) {
          console.error("Error formatting DOB:", e);
        }
      }

      // Populate form with current row data
      this.newAlumini = {
        id: this.currentRow.id,
        year: this.currentRow.regID,
        familyID: this.currentRow.familyID || "",
        studentID: this.currentRow.studentID || "",
        fatherPersonID: this.currentRow.fatherPersonID || "",
        class: this.currentRow.class || "H-11",
        section: this.currentRow.section || "",
        branch: this.currentRow.branch || null,
        firstHebrew: this.currentRow.firstHebrew || "",
        lastHebrew: this.currentRow.lastHebrew || "",
        engName: this.currentRow.engName || "",
        dob: formattedDob,
        lastEnglish: this.currentRow.lastEnglish || "",
      };

      // Fetch person details if fatherPersonID exists
      if (this.currentRow.fatherPersonID) {
        try {
          const person = await this.$http.get(
            "api/donors/GetDonor?personID=" +
              encodeURIComponent(this.currentRow.fatherPersonID)
          );
          this.selectedPerson = person;
        } catch (error) {
          console.error("Error fetching person details:", error);
        }
      }

      // Fetch account details if familyID exists
      if (
        this.currentRow.familyID &&
        !this.currentRow.familyID.startsWith("MISSING_FAMILY_ID")
      ) {
        try {
          const account = await this.$http.get(
            "api/accounts/GetAccount?accountID=" +
              encodeURIComponent(this.currentRow.familyID)
          );
          this.selectedFamily = account;
        } catch (error) {
          console.error("Error fetching account details:", error);
        }
      }

      this.addDialog = true;

      // Reset validation on next tick after dialog opens
      this.$nextTick(() => {
        if (this.$refs.aluminiForm) {
          this.$refs.aluminiForm.resetValidation();
        }
      });
    },
    goToDinnerView() {
      this.editOptionsDialog = false;
      this.$router.push({
        name: "DinnerAccountView",
        params: {
          id: this.currentRow.fatherPersonID,
        },
        query: {
          aluminiStudent: this.currentRow.studentID || this.currentRow.id,
          limitView: true,
        },
      });
    },
    closeDialog() {
      // If in batch mode and canceling an edit, return to add mode instead of closing
      if (this.isEditMode && this.batchSettings.year) {
        this.isEditMode = false;
        this.currentRow = null;
        this.resetForm();

        // Restore batch settings
        this.newAlumini.year = this.batchSettings.year;
        this.newAlumini.class = this.batchSettings.class;
        this.newAlumini.section = this.batchSettings.section;
        this.newAlumini.branch = this.batchSettings.branch;

        // Clear selected persons
        this.selectedPerson = null;
        this.selectedFamily = null;

        // Reset validation and focus
        this.$nextTick(() => {
          if (this.$refs.aluminiForm) {
            this.$refs.aluminiForm.resetValidation();
          }

          const lastNameField = document.querySelector(
            'input[aria-label="Last Name (English) *"]'
          );
          if (lastNameField) {
            lastNameField.focus();
          }
        });
        return;
      }

      // Normal close - close everything
      this.addDialog = false;
      this.errorMessage = "";
      this.successMessage = "";
      this.isEditMode = false;
      this.currentRow = null;
      this.batchSettings = {
        year: null,
        class: "H-11",
        section: "",
        branch: null,
      };
      this.recentlyAdded = [];
      this.resetForm();
    },
    closeEditOptionsDialog() {
      this.editOptionsDialog = false;
      this.currentRow = null;
    },
    resetForm() {
      this.newAlumini = {
        id: null,
        year: null,
        familyID: "",
        studentID: "",
        fatherPersonID: "",
        class: "H-11",
        section: "",
        branch: null,
        firstHebrew: "",
        lastHebrew: "",
        engName: "",
        dob: null,
        lastEnglish: "",
      };
      this.pickerDate = null;
      this.selectedPerson = null;
      this.selectedFamily = null;
      this.errorMessage = "";
      this.successMessage = "";

      // Reset validation
      this.$nextTick(() => {
        if (this.$refs.aluminiForm) {
          this.$refs.aluminiForm.resetValidation();
        }
      });
    },
    onYearChanged(year) {
      if (year) {
        // Extract numeric year from RG prefix (e.g., "RG2026" -> 2026)
        const numericYear = parseInt(year.replace("RG", ""));
        // Set date picker to 17 years prior
        const dobYear = numericYear - 17;
        this.pickerDate = dobYear + "-01";
      }
    },
    openPersonSearch() {
      this.personSearchDialog = true;
      // Don't reset - keep previous search state
    },
    closePersonSearch() {
      this.personSearchDialog = false;
      // Don't reset - keep search state for next time
    },
    resetPersonSearch() {
      this.personSearchType = "phone";
      this.personSearchNameMode = "english";
      this.personPhoneSearch = "";
      this.personFirstNameSearch = "";
      this.personLastNameSearch = "";
      this.personAddressSearch = "";
      this.personCitySearch = "";
      this.personZipSearch = "";
      this.searchedPersons = [];
      this.showNoPersonsMessage = false;
      if (this.personSearchDebounceTimer) {
        clearTimeout(this.personSearchDebounceTimer);
      }
    },
    async searchPersonByPhone(value) {
      if (!value || value.length < 3) {
        this.searchedPersons = [];
        this.showNoPersonsMessage = false;
        return;
      }

      if (this.personSearchDebounceTimer) {
        clearTimeout(this.personSearchDebounceTimer);
      }

      this.personSearchDebounceTimer = setTimeout(async () => {
        try {
          const response = await this.$http.get(
            "api/donors/GetAccountByPhoneNumber?phoneNumber=" +
              encodeURIComponent(value)
          );
          this.searchedPersons = response || [];
          this.showNoPersonsMessage = this.searchedPersons.length === 0;
        } catch (error) {
          console.error("Error searching by phone:", error);
          this.searchedPersons = [];
          this.showNoPersonsMessage = true;
        }
      }, 400);
    },
    async searchPersonByName() {
      const first = this.personFirstNameSearch;
      const last = this.personLastNameSearch;

      if (!first && !last) {
        this.searchedPersons = [];
        this.showNoPersonsMessage = false;
        return;
      }

      if (this.personSearchDebounceTimer) {
        clearTimeout(this.personSearchDebounceTimer);
      }

      this.personSearchDebounceTimer = setTimeout(async () => {
        try {
          const isYiddish = this.personSearchNameMode === "yiddish";
          const response = await this.$http.get(
            "api/donors/GetAccountsByName?last=" +
              encodeURIComponent(last || "") +
              "&first=" +
              encodeURIComponent(first || "") +
              "&isYiddish=" +
              isYiddish
          );
          this.searchedPersons = response || [];
          this.showNoPersonsMessage = this.searchedPersons.length === 0;
        } catch (error) {
          console.error("Error searching by name:", error);
          this.searchedPersons = [];
          this.showNoPersonsMessage = true;
        }
      }, 400);
    },
    async searchPersonByAddress() {
      const address = this.personAddressSearch;
      const city = this.personCitySearch;
      const zip = this.personZipSearch;

      if (!address && !city && !zip) {
        this.searchedPersons = [];
        this.showNoPersonsMessage = false;
        return;
      }

      if (this.personSearchDebounceTimer) {
        clearTimeout(this.personSearchDebounceTimer);
      }

      this.personSearchDebounceTimer = setTimeout(async () => {
        try {
          const response = await this.$http.get(
            "api/donors/GetAccountsByAddress?address1=" +
              encodeURIComponent(address || "") +
              "&city=" +
              encodeURIComponent(city || "") +
              "&zipcode=" +
              encodeURIComponent(zip || "")
          );
          this.searchedPersons = response || [];
          this.showNoPersonsMessage = this.searchedPersons.length === 0;
        } catch (error) {
          console.error("Error searching by address:", error);
          this.searchedPersons = [];
          this.showNoPersonsMessage = true;
        }
      }, 400);
    },
    onPersonSelected(person) {
      if (person && person.personID) {
        this.newAlumini.fatherPersonID = person.personID;
        this.selectedPerson = person;
        // Close dialog after selection, but keep state
        this.closePersonSearch();
      }
    },
    clearFatherPerson() {
      this.newAlumini.fatherPersonID = "";
      this.selectedPerson = null;
      // Don't clear search state - only clear on save
    },
    reopenPersonSearch() {
      // Reopen the person search with existing results
      this.personSearchDialog = true;
    },
    openFamilySearch() {
      this.familySearchDialog = true;
      // Don't reset - keep previous search state
    },
    closeFamilySearch() {
      this.familySearchDialog = false;
      // Don't reset - keep search state for next time
    },
    resetFamilySearch() {
      this.familySearchType = "phone";
      this.familySearchNameMode = "english";
      this.familyPhoneSearch = "";
      this.familyFirstNameSearch = "";
      this.familyLastNameSearch = "";
      this.familyAddressSearch = "";
      this.familyCitySearch = "";
      this.familyZipSearch = "";
      this.searchedFamilies = [];
      this.showNoFamilyMessage = false;
      if (this.familySearchDebounceTimer) {
        clearTimeout(this.familySearchDebounceTimer);
      }
    },
    async searchFamilyByPhone(value) {
      if (!value || value.length < 3) {
        this.searchedFamilies = [];
        this.showNoFamilyMessage = false;
        return;
      }

      if (this.familySearchDebounceTimer) {
        clearTimeout(this.familySearchDebounceTimer);
      }

      this.familySearchDebounceTimer = setTimeout(async () => {
        try {
          const response = await this.$http.get(
            "api/accounts/GetAccountByPhoneNumber?phoneNumber=" +
              encodeURIComponent(value)
          );
          this.searchedFamilies = response || [];
          this.showNoFamilyMessage = this.searchedFamilies.length === 0;
        } catch (error) {
          console.error("Error searching by phone:", error);
          this.searchedFamilies = [];
          this.showNoFamilyMessage = true;
        }
      }, 400);
    },
    async searchFamilyByName() {
      const first = this.familyFirstNameSearch;
      const last = this.familyLastNameSearch;

      if (!first && !last) {
        this.searchedFamilies = [];
        this.showNoFamilyMessage = false;
        return;
      }

      if (this.familySearchDebounceTimer) {
        clearTimeout(this.familySearchDebounceTimer);
      }

      this.familySearchDebounceTimer = setTimeout(async () => {
        try {
          const isYiddish = this.familySearchNameMode === "yiddish";
          const response = await this.$http.get(
            "api/accounts/GetAccountsByName?last=" +
              encodeURIComponent(last || "") +
              "&first=" +
              encodeURIComponent(first || "") +
              "&isYiddish=" +
              isYiddish
          );
          this.searchedFamilies = response || [];
          this.showNoFamilyMessage = this.searchedFamilies.length === 0;
        } catch (error) {
          console.error("Error searching by name:", error);
          this.searchedFamilies = [];
          this.showNoFamilyMessage = true;
        }
      }, 400);
    },
    async searchFamilyByAddress() {
      const address = this.familyAddressSearch;
      const city = this.familyCitySearch;
      const zip = this.familyZipSearch;

      if (!address && !city && !zip) {
        this.searchedFamilies = [];
        this.showNoFamilyMessage = false;
        return;
      }

      if (this.familySearchDebounceTimer) {
        clearTimeout(this.familySearchDebounceTimer);
      }

      this.familySearchDebounceTimer = setTimeout(async () => {
        try {
          const response = await this.$http.get(
            "api/accounts/GetAccountsByAddress?address1=" +
              encodeURIComponent(address || "") +
              "&city=" +
              encodeURIComponent(city || "") +
              "&zipcode=" +
              encodeURIComponent(zip || "")
          );
          this.searchedFamilies = response || [];
          this.showNoFamilyMessage = this.searchedFamilies.length === 0;
        } catch (error) {
          console.error("Error searching by address:", error);
          this.searchedFamilies = [];
          this.showNoFamilyMessage = true;
        }
      }, 400);
    },
    onFamilySelected(family) {
      if (family && family.accountID) {
        this.newAlumini.familyID = family.accountID;
        this.selectedFamily = family;
        // Close dialog after selection, but keep state
        this.closeFamilySearch();
      }
    },
    clearFamilyID() {
      this.newAlumini.familyID = "";
      this.selectedFamily = null;
      // Don't clear search state - only clear on save
    },
    reopenFamilySearch() {
      // Reopen the family search with existing results
      this.familySearchDialog = true;
    },
    isFormValid() {
      // In batch mode, year/class/section come from batchSettings
      // In edit mode, they come from newAlumini
      const hasYearClassSection = this.isEditMode
        ? this.newAlumini.year &&
          this.newAlumini.class &&
          this.newAlumini.section
        : this.batchSettings.year &&
          this.batchSettings.class &&
          this.batchSettings.section;

      return (
        hasYearClassSection &&
        this.newAlumini.engName &&
        this.newAlumini.lastEnglish
      );
    },
    formatTime(date) {
      if (!date) return "";
      const now = new Date();
      const diff = Math.floor((now - date) / 1000); // seconds

      if (diff < 60) return "Just now";
      if (diff < 3600) return Math.floor(diff / 60) + "m ago";
      if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
      return date.toLocaleDateString();
    },
    generateYearsList() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 50; i++) {
        years.push("RG" + (currentYear - i));
      }
      this.yearsList = years;
    },
    async loadBranchList() {
      try {
        const branches = await this.$http.get("api/students/getBranchList");
        this.branchList = branches.sort();
      } catch (error) {
        console.error("Error loading branch list:", error);
        this.branchList = [];
      }
    },
    async saveAlumini(continueEditing = false) {
      if (!this.isFormValid()) {
        this.errorMessage = "Please fill in all required fields";
        return;
      }

      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const data = {
          Id: this.newAlumini.id || 0,
          RegID: this.newAlumini.year, // Already includes RG prefix
          FamilyID: this.newAlumini.familyID || null,
          StudentID: this.newAlumini.studentID,
          personID: this.newAlumini.fatherPersonID || null,
          Class: this.newAlumini.class || null,
          Section: this.newAlumini.section || null,
          Branch: this.newAlumini.branch || null,
          FirstHebrew: this.newAlumini.firstHebrew || null,
          LastHebrew: this.newAlumini.lastHebrew || null,
          EngName: this.newAlumini.engName || null,
          Dob: this.newAlumini.dob || null,
          LastEnglish: this.newAlumini.lastEnglish || null,
          createdBy: this.$store.state.authentication.user,
        };

        const response = await this.$http.post("api/Alumini/SaveAlumini", data);

        this.successMessage = this.isEditMode
          ? "Alumini record updated successfully!"
          : "Alumini record saved successfully!";

        // If editing a recently added record, update it in the list
        if (this.isEditMode && this.newAlumini.id) {
          const recentIndex = this.recentlyAdded.findIndex(
            (r) => r.id === this.newAlumini.id
          );
          if (recentIndex !== -1) {
            this.recentlyAdded[recentIndex] = {
              id: this.newAlumini.id,
              engName: this.newAlumini.engName,
              lastEnglish: this.newAlumini.lastEnglish,
              firstHebrew: this.newAlumini.firstHebrew,
              lastHebrew: this.newAlumini.lastHebrew,
              studentID: this.newAlumini.studentID,
              familyID: this.newAlumini.familyID,
              fatherPersonID: this.newAlumini.fatherPersonID,
              branch: this.newAlumini.branch,
              class: this.newAlumini.class,
              section: this.newAlumini.section,
              regID: this.newAlumini.year,
              dob: this.newAlumini.dob,
              createdOn: this.recentlyAdded[recentIndex].createdOn,
              addedAt: this.recentlyAdded[recentIndex].addedAt,
            };
          }
        }

        // Refresh the grid
        if (this.$refs.dxGrid && this.$refs.dxGrid.reloadGrid) {
          this.$refs.dxGrid.reloadGrid();
        }

        if (continueEditing && !this.isEditMode) {
          // Add to recently added list with full details
          // Use response ID if available, otherwise use the data ID
          const recordId = response && response.id ? response.id : data.Id;
          this.recentlyAdded.unshift({
            id: recordId,
            engName: this.newAlumini.engName,
            lastEnglish: this.newAlumini.lastEnglish,
            firstHebrew: this.newAlumini.firstHebrew,
            lastHebrew: this.newAlumini.lastHebrew,
            studentID: this.newAlumini.studentID,
            familyID: this.newAlumini.familyID,
            fatherPersonID: this.newAlumini.fatherPersonID,
            branch: this.newAlumini.branch,
            class: this.newAlumini.class,
            section: this.newAlumini.section,
            regID: this.newAlumini.year,
            dob: this.newAlumini.dob,
            createdOn: new Date(),
            addedAt: new Date(),
          });

          // Keep only last 20 records
          if (this.recentlyAdded.length > 20) {
            this.recentlyAdded = this.recentlyAdded.slice(0, 20);
          }

          this.resetForm();

          // Restore batch settings
          this.newAlumini.year = this.batchSettings.year;
          this.newAlumini.class = this.batchSettings.class;
          this.newAlumini.section = this.batchSettings.section;
          this.newAlumini.branch = this.batchSettings.branch;

          // Update picker date based on year
          this.onYearChanged(this.batchSettings.year);

          // Clear selected persons and search results for next entry
          this.selectedPerson = null;
          this.selectedFamily = null;
          this.searchedPersons = [];
          this.searchedFamilies = [];

          // Clear all search input fields
          this.personPhoneSearch = "";
          this.personFirstNameSearch = "";
          this.personLastNameSearch = "";
          this.personAddressSearch = "";
          this.personCitySearch = "";
          this.personZipSearch = "";
          this.familyPhoneSearch = "";
          this.familyFirstNameSearch = "";
          this.familyLastNameSearch = "";
          this.familyAddressSearch = "";
          this.familyCitySearch = "";
          this.familyZipSearch = "";

          // Clear success message after a short delay
          setTimeout(() => {
            this.successMessage = "";
          }, 1500);

          // Focus on last name field for quick entry
          this.$nextTick(() => {
            const lastNameField = document.querySelector(
              'input[aria-label="Last Name (English) *"]'
            );
            if (lastNameField) {
              lastNameField.focus();
            }
          });
        } else {
          // Check if we're in batch mode and editing a recently added record
          const wasRecentlyAdded =
            this.isEditMode &&
            this.batchSettings.year &&
            this.recentlyAdded.some((r) => r.id === this.newAlumini.id);

          if (wasRecentlyAdded) {
            // Close edit options and return to batch entry mode
            setTimeout(() => {
              this.editOptionsDialog = false;
              this.isEditMode = false; // Switch back to add mode
              this.currentRow = null;
              this.resetForm();

              // Restore batch settings
              this.newAlumini.year = this.batchSettings.year;
              this.newAlumini.class = this.batchSettings.class;
              this.newAlumini.section = this.batchSettings.section;
              this.newAlumini.branch = this.batchSettings.branch;

              // Clear search states for next entry
              this.selectedPerson = null;
              this.selectedFamily = null;

              this.successMessage = "";

              // Reset validation and focus on last name field for next entry
              this.$nextTick(() => {
                if (this.$refs.aluminiForm) {
                  this.$refs.aluminiForm.resetValidation();
                }

                const lastNameField = document.querySelector(
                  'input[aria-label="Last Name (English) *"]'
                );
                if (lastNameField) {
                  lastNameField.focus();
                }
              });
            }, 1500);
          } else {
            // Normal mode - close everything
            setTimeout(() => {
              this.closeDialog();
            }, 1500);
          }
        }
      } catch (error) {
        console.error("Error saving alumini:", error);
        this.errorMessage = "Failed to save alumini record. Please try again.";
      } finally {
        this.saving = false;
      }
    },
    async saveAluminiAndClose() {
      if (!this.isFormValid()) {
        this.errorMessage = "Please fill in all required fields";
        return;
      }

      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const data = {
          Id: this.newAlumini.id || 0,
          RegID: this.newAlumini.year,
          FamilyID: this.newAlumini.familyID || null,
          StudentID: this.newAlumini.studentID,
          personID: this.newAlumini.fatherPersonID || null,
          Class: this.newAlumini.class || null,
          Section: this.newAlumini.section || null,
          Branch: this.newAlumini.branch || null,
          FirstHebrew: this.newAlumini.firstHebrew || null,
          LastHebrew: this.newAlumini.lastHebrew || null,
          EngName: this.newAlumini.engName || null,
          Dob: this.newAlumini.dob || null,
          LastEnglish: this.newAlumini.lastEnglish || null,
          createdBy: this.$store.state.authentication.user,
        };

        await this.$http.post("api/Alumini/SaveAlumini", data);

        // Refresh the grid
        if (this.$refs.dxGrid && this.$refs.dxGrid.reloadGrid) {
          this.$refs.dxGrid.reloadGrid();
        }

        // Show success notification
        this.successMessage = "Alumini record saved successfully!";

        // Close dialog after a short delay
        setTimeout(() => {
          this.closeDialog();
        }, 1500);
      } catch (error) {
        console.error("Error saving alumini:", error);
        this.errorMessage = "Failed to save alumini record. Please try again.";
      } finally {
        this.saving = false;
      }
    },
    confirmDelete() {
      this.deleteConfirmDialog = true;
    },
    async deleteAlumini() {
      if (!this.newAlumini.id) {
        this.errorMessage = "Cannot delete: No ID found";
        return;
      }

      this.deleting = true;
      this.errorMessage = "";

      // Check if this is a recently added record
      const isRecentlyAdded = this.recentlyAdded.some(
        (r) => r.id === this.newAlumini.id
      );

      try {
        await this.$http.post(
          "api/alumini/delete?id=" + encodeURIComponent(this.newAlumini.id)
        );

        this.deleteConfirmDialog = false;

        // If it was a recently added record, remove from the array
        if (isRecentlyAdded) {
          this.recentlyAdded = this.recentlyAdded.filter(
            (r) => r.id !== this.newAlumini.id
          );
        }

        // Refresh the grid
        if (this.$refs.dxGrid && this.$refs.dxGrid.reloadGrid) {
          this.$refs.dxGrid.reloadGrid();
        }

        // Show success message
        this.successMessage = "Alumini record deleted successfully!";
        setTimeout(() => {
          this.successMessage = "";
        }, 2000);

        // If in batch mode (recently added), stay in dialog and reset for next entry
        if (isRecentlyAdded && this.batchSettings.year) {
          setTimeout(() => {
            this.editOptionsDialog = false;
            this.isEditMode = false; // Switch back to add mode
            this.currentRow = null;
            this.resetForm();

            // Restore batch settings
            this.newAlumini.year = this.batchSettings.year;
            this.newAlumini.class = this.batchSettings.class;
            this.newAlumini.section = this.batchSettings.section;
            this.newAlumini.branch = this.batchSettings.branch;

            // Clear search states for next entry
            this.selectedPerson = null;
            this.selectedFamily = null;

            this.successMessage = "";

            // Reset validation and focus on last name field for next entry
            this.$nextTick(() => {
              if (this.$refs.aluminiForm) {
                this.$refs.aluminiForm.resetValidation();
              }

              const lastNameField = document.querySelector(
                'input[aria-label="Last Name (English) *"]'
              );
              if (lastNameField) {
                lastNameField.focus();
              }
            });
          }, 1500);
        } else {
          // Normal mode - close everything
          this.addDialog = false;
          this.closeDialog();
        }
      } catch (error) {
        console.error("Error deleting alumini:", error);
        this.errorMessage =
          "Failed to delete alumini record. Please try again.";
        this.deleteConfirmDialog = false;
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
<style scoped>
/* Compact Fields */
.compact-field >>> .v-input__control {
  min-height: 40px !important;
}

.compact-field >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 8px !important;
}

.compact-field >>> .v-text-field__details {
  padding-top: 2px !important;
  margin-bottom: 0 !important;
}

.py-0 {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

/* Mini Person Cards */
.mini-person-card {
  background-color: #f9f9f9;
  border-left: 2px solid #4caf50;
}

.person-label {
  min-width: 60px;
  color: #4caf50;
}

/* Sidebar Section */
.sidebar-section {
  border-left: 1px solid #e0e0e0;
}

.recent-list {
  max-height: 500px;
  overflow-y: auto;
}

.recent-item {
  background: white;
  border-radius: 3px;
  border: 1px solid #e0e0e0;
  min-height: 60px !important;
  height: auto !important;
}

/* Person Search Dialogs */
.person-list-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
}

.person-list {
  background: transparent;
  padding: 0;
}

.person-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
  padding: 8px 10px;
  min-height: auto;
}

.person-item:hover {
  background-color: #e8f5e9 !important;
}

.person-item:last-child {
  border-bottom: none;
}

.person-name {
  line-height: 1.6;
  margin-bottom: 6px;
  word-wrap: break-word;
}

.person-details {
  line-height: 1.6;
}

.detail-line {
  display: flex;
  align-items: flex-start;
  margin-bottom: 2px;
}

.detail-line:last-child {
  margin-bottom: 0;
}

.detail-icon {
  margin-right: 6px;
  margin-top: 2px;
  flex-shrink: 0;
}

.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}
</style>
